"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

// ── Types ──────────────────────────────────────────────────────────────────

export interface Flow {
  id: string;
  org_id: string;
  name: string;
  greeting_message: string | null;
  min_score_for_cpa: number;
  active: boolean;
  created_at: string;
}

export interface UploadedDoc {
  filename: string;
  uploaded_at: string;
}

export interface MemoryBank {
  id: string;
  flow_id: string;
  company_description: string | null;
  products_services: string | null;
  pricing: string | null;
  icp: string | null;
  knowledge_urls: string[];
  faqs: string | null;
  objections: string | null;
  policies: string | null;
  communication_tone: string | null;
  uploaded_docs: UploadedDoc[];
}

export interface QuestionOption {
  id: string;
  concept_label: string;
  score_value: number;
  semantic_aliases: string[];
}

export interface Question {
  id: string;
  flow_id: string;
  order_index: number;
  question_text: string;
  question_type: string;
  max_retries: number;
  options: QuestionOption[];
}

export interface Lead {
  id: string;
  org_id: string;
  flow_id: string | null;
  name: string | null;
  phone: string;
  email: string | null;
  company: string | null;
  campaign: string | null;
  origin: string | null;
  lead_type: string | null;
  total_score: number;
  max_possible_score: number;
  agent_state: string | null;
  created_at: string;
}

export interface LeadList {
  items: Lead[];
  total: number;
  page: number;
  page_size: number;
}

export interface Message {
  id: string;
  conversation_id: string;
  direction: string;
  sender_type: string;
  content: string | null;
  message_type: string;
  sent_at: string;
}

export interface Conversation {
  id: string;
  lead_id: string;
  state: string;
  current_question_index: number;
  human_operator_id: string | null;
  last_message_at: string | null;
  created_at: string;
  lead_name: string | null;
  lead_phone: string | null;
  lead_type: string | null;
  agent_state: string | null;
  total_score: number | null;
  max_possible_score: number | null;
  sentiment: "positive" | "negative" | "neutral" | null;
  handoff_summary: string | null;
  flow_id: string | null;
  flow_name: string | null;
  campaign: string | null;
  origin: string | null;
  messages?: Message[];
}

export interface ConversationList {
  items: Conversation[];
  total: number;
  page: number;
  page_size: number;
}

export interface DashboardStats {
  total_conversations: number;
  qualified: number;
  almost_qualified: number;
  not_qualified: number;
  incomplete: number;
  conversion_rate: number;
  breakdown: { lead_type: string; count: number; percentage: number }[];
  daily_series: { date: string; conversations: number; qualified: number }[];
}

export interface WhatsAppStatus {
  connected: boolean;
  phone_display: string | null;
  waba_id: string | null;
  phone_number_id: string | null;
}

// ── Flows ──────────────────────────────────────────────────────────────────

export function useFlows() {
  return useQuery<Flow[]>({
    queryKey: ["flows"],
    queryFn: () => api.get("/flows"),
  });
}

export function useFlow(id: string) {
  return useQuery<Flow & { memory_bank?: MemoryBank; questions?: Question[] }>({
    queryKey: ["flows", id],
    queryFn: () => api.get(`/flows/${id}`),
    enabled: !!id,
  });
}

export function useCreateFlow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; greeting_message?: string }) =>
      api.post<Flow>("/flows", data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["flows"] }),
  });
}

export function useUpdateFlow(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Flow>) => api.put<Flow>(`/flows/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["flows"] });
      qc.invalidateQueries({ queryKey: ["flows", id] });
    },
  });
}

export function useDeleteFlow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/flows/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["flows"] }),
  });
}

// ── Memory Bank ────────────────────────────────────────────────────────────

export function useMemoryBank(flowId: string) {
  return useQuery<MemoryBank>({
    queryKey: ["memory-bank", flowId],
    queryFn: () => api.get(`/flows/${flowId}/memory-bank`),
    enabled: !!flowId,
  });
}

export function useUpdateMemoryBank(flowId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<MemoryBank>) =>
      api.put<MemoryBank>(`/flows/${flowId}/memory-bank`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["memory-bank", flowId] }),
  });
}

export function useUploadMemoryBankDoc(flowId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => {
      const fd = new FormData();
      fd.append("file", file);
      return api.upload<MemoryBank>(`/flows/${flowId}/memory-bank/upload-doc`, fd);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["memory-bank", flowId] }),
  });
}

export function useDeleteMemoryBankDoc(flowId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (docIndex: number) =>
      api.delete<MemoryBank>(`/flows/${flowId}/memory-bank/docs/${docIndex}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["memory-bank", flowId] }),
  });
}

// ── Questions ──────────────────────────────────────────────────────────────

export function useQuestions(flowId: string) {
  return useQuery<Question[]>({
    queryKey: ["questions", flowId],
    queryFn: () => api.get(`/flows/${flowId}/questions`),
    enabled: !!flowId,
  });
}

export function useCreateQuestion(flowId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Omit<Question, "id" | "flow_id" | "order_index">) =>
      api.post<Question>(`/flows/${flowId}/questions`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["questions", flowId] }),
  });
}

export function useDeleteQuestion(flowId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (questionId: string) =>
      api.delete(`/flows/${flowId}/questions/${questionId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["questions", flowId] }),
  });
}

export function useUpdateQuestion(flowId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ questionId, data }: { questionId: string; data: Partial<Question> & { options?: Omit<QuestionOption, 'id'>[] } }) =>
      api.put<Question>(`/flows/${flowId}/questions/${questionId}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["questions", flowId] }),
  });
}

// ── CPA Config ─────────────────────────────────────────────────────────────

export interface CPAConfig {
  id: string;
  flow_id: string;
  cpa_type: string | null;
  cpa_link: string | null;
  cpa_launch_text: string | null;
  min_score: number | null;
}

export function useCPAConfig(flowId: string) {
  return useQuery<CPAConfig>({
    queryKey: ["cpa", flowId],
    queryFn: () => api.get(`/flows/${flowId}/cpa`),
    enabled: !!flowId,
  });
}

export function useUpdateCPAConfig(flowId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<CPAConfig>) => api.put<CPAConfig>(`/flows/${flowId}/cpa`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["cpa", flowId] }),
  });
}

// ── Reactivation Config ────────────────────────────────────────────────────

export interface ReactivationConfig {
  id: string;
  flow_id: string;
  almost_qualified_hours: number | null;
  almost_qualified_max_attempts: number | null;
  almost_qualified_interval_hours: number | null;
  almost_qualified_message: string | null;
  incomplete_hours: number | null;
  incomplete_max_attempts: number | null;
  incomplete_interval_hours: number | null;
  incomplete_message: string | null;
  business_hours_start: string | null;
  business_hours_end: string | null;
  timezone: string | null;
}

export function useReactivationConfig(flowId: string) {
  return useQuery<ReactivationConfig>({
    queryKey: ["reactivation", flowId],
    queryFn: () => api.get(`/flows/${flowId}/reactivation`),
    enabled: !!flowId,
  });
}

export function useUpdateReactivationConfig(flowId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<ReactivationConfig>) =>
      api.put<ReactivationConfig>(`/flows/${flowId}/reactivation`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["reactivation", flowId] }),
  });
}

// ── Closure Config ─────────────────────────────────────────────────────────

export interface ClosureConfig {
  id: string;
  flow_id: string;
  qualified_close_message: string | null;
  not_qualified_close_message: string | null;
}

export function useClosureConfig(flowId: string) {
  return useQuery<ClosureConfig>({
    queryKey: ["closures", flowId],
    queryFn: () => api.get(`/flows/${flowId}/closures`),
    enabled: !!flowId,
  });
}

export function useUpdateClosureConfig(flowId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<ClosureConfig>) => api.put<ClosureConfig>(`/flows/${flowId}/closures`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["closures", flowId] }),
  });
}

// ── Leads ──────────────────────────────────────────────────────────────────

export function useLeads(params?: {
  page?: number;
  lead_type?: string;
  search?: string;
}) {
  const q = new URLSearchParams();
  if (params?.page) q.set("page", String(params.page));
  if (params?.lead_type) q.set("lead_type", params.lead_type);
  if (params?.search) q.set("search", params.search);
  const qs = q.toString();

  return useQuery<LeadList>({
    queryKey: ["leads", params],
    queryFn: () => api.get(`/leads${qs ? `?${qs}` : ""}`),
  });
}

export function useLead(id: string) {
  return useQuery<Lead>({
    queryKey: ["leads", id],
    queryFn: () => api.get(`/leads/${id}`),
    enabled: !!id,
  });
}

// ── Conversations ──────────────────────────────────────────────────────────

export function useConversations(params?: {
  state?: string;
  search?: string;
  lead_type?: string;
  agent_state?: string;
  flow_id?: string;
  campaign?: string;
  sentiment?: string;
}) {
  const q = new URLSearchParams();
  if (params?.state) q.set("state", params.state);
  if (params?.search) q.set("search", params.search);
  if (params?.lead_type) q.set("lead_type", params.lead_type);
  if (params?.agent_state) q.set("agent_state", params.agent_state);
  if (params?.flow_id) q.set("flow_id", params.flow_id);
  if (params?.campaign) q.set("campaign", params.campaign);
  if (params?.sentiment) q.set("sentiment", params.sentiment);
  const qs = q.toString();

  return useQuery<ConversationList>({
    queryKey: ["conversations", params],
    queryFn: () => api.get(`/conversations${qs ? `?${qs}` : ""}`),
    refetchInterval: 15000,
  });
}

export function useConversation(id: string) {
  return useQuery<Conversation>({
    queryKey: ["conversations", id],
    queryFn: () => api.get(`/conversations/${id}`),
    enabled: !!id,
    refetchInterval: 5000,
  });
}

export function useTakeControl(conversationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post(`/conversations/${conversationId}/take-control`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["conversations"] }),
  });
}

export function useReleaseControl(conversationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post(`/conversations/${conversationId}/release-control`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["conversations"] }),
  });
}

export function useSendMessage(conversationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (content: string) =>
      api.post(`/conversations/${conversationId}/messages`, { content }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["conversations"] }),
  });
}

// ── Dashboard ──────────────────────────────────────────────────────────────

export function useDashboard() {
  return useQuery<DashboardStats>({
    queryKey: ["dashboard"],
    queryFn: () => api.get("/dashboard/stats"),
    refetchInterval: 30000,
  });
}

// ── WhatsApp ───────────────────────────────────────────────────────────────

export function useWhatsAppStatus() {
  return useQuery<WhatsAppStatus>({
    queryKey: ["whatsapp-status"],
    queryFn: () => api.get("/whatsapp/status"),
  });
}

export function useConnectWhatsApp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      waba_id: string;
      phone_number_id: string;
      access_token: string;
      phone_display?: string;
    }) => api.post("/whatsapp/connect", data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["whatsapp-status"] }),
  });
}
